import {
  Button,
  PageBody,
  PageSection,
  Snackbar,
  Spacer,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '../../../components';
import { DeleteTeamConfirmationModal } from '../../../components/modals/delete-team-confirmation-modal/delete-team-confirmation-modal';
import {
  sendManagerEvent,
  useManagerCtxSelector,
  useManagerStateMatches,
  useManagerStateMatchesOneOf,
} from '../../../xstate/app.xstate';
import { IPage } from '../../types';
import { TeamInfo } from './team-info';
import { TeamUsers } from './team-users';
import { PermissionsTable } from '../../../components/permissions-table';

type ITeamDetailsProps = IPage & {
  isNewTeam?: boolean;
};

export const TeamDetails: FC<ITeamDetailsProps> = ({ isNewTeam }) => {
  const params = useParams();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { org_id: orgIdQueryParam = '', team_id: teamIdQueryParam = '' } =
    params;

  const { teamDetails, selectedTeam } = useManagerCtxSelector((ctx) => ({
    teamDetails: ctx.teamDetails,
    selectedTeam: ctx.selectedTeam,
  }));
  const { state: teamDetailsState, teamDetailUpdates: updates } = teamDetails[
    teamIdQueryParam
  ] || {
    state: null,
    teamDetailUpdates: null,
  };
  const { availableUsers } = teamDetailsState || { availableUsers: [] };
  const isIdle = useManagerStateMatches('orgRelated.ready.teamDetails.idle');
  const loadingTeamDetails = Boolean(
    useManagerStateMatchesOneOf([
      'orgRelated.ready.teamDetails.loading',
      'orgRelated.ready.teamDetails.initiateNewTeam',
    ])
  );
  const hasErrors = useManagerStateMatches(
    'orgRelated.ready.teamDetails.error'
  );
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
    },
    shouldUseNativeValidation: true,
  });
  const {
    reset: resetInfoForm,
    trigger: triggerTeamInfoDetailsForm,
    getValues: getTeamInfoDetailsValues,
  } = form;

  useEffect(() => {
    resetInfoForm({
      name: teamDetailsState?.name || '',
      description: teamDetailsState?.description || '',
    });
  }, [teamDetailsState?.name, teamDetailsState?.description, resetInfoForm]);

  const savingUpdates = Boolean(
    useManagerStateMatchesOneOf([
      'orgRelated.ready.teamDetails.saveUpdates',
      'orgRelated.ready.teamDetails.createNewTeam',
    ])
  );

  const [showDeleteTeamConfirmationModal, setShowDeleteTeamConfirmationModal] =
    useState(false);

  const isDeletingTeam = useManagerStateMatches(
    'orgRelated.ready.teamDetails.deleteTeam'
  );

  const isTeamDeleted = useManagerStateMatches(
    'orgRelated.ready.teamDetails.teamDeleted'
  );

  const isTeamCreated = useManagerStateMatches(
    'orgRelated.ready.teamDetails.teamCreated'
  );

  useEffect(() => {
    if (isTeamDeleted) {
      sendManagerEvent({
        type: 'manager.teamDetails.clearTeamState',
        teamId: teamIdQueryParam,
      });
      navigate(`/organization/${orgIdQueryParam}/teams`);
    }
  }, [isTeamDeleted, navigate, orgIdQueryParam, teamIdQueryParam]);

  useEffect(() => {
    if (isTeamCreated && selectedTeam) {
      navigate(`/organization/${orgIdQueryParam}/teams/${selectedTeam}`);
    }
  }, [isTeamCreated, navigate, orgIdQueryParam, selectedTeam]);

  if (hasErrors) {
    return (
      <PageBody>
        <TextTitle size="small" i18nKey="manager/common:genericError">
          Something went wrong: Try again
        </TextTitle>
      </PageBody>
    );
  }

  if (!teamDetailsState) {
    if (loadingTeamDetails || isIdle) {
      return (
        <PageBody background="none">
          <PageSection size="small">
            <Loader relative />
          </PageSection>
        </PageBody>
      );
    }
    return null;
  }

  return (
    <PageBody background="none">
      <TextTitle test-id="team-details-subtitle">
        {(isNewTeam && (
          <TranslationComponent i18nKey="manager/teams:teamDetails.createTeam">
            Create Team
          </TranslationComponent>
        )) ||
          `${teamDetailsState.name}`}
      </TextTitle>
      <Spacer size="large" />
      <>
        <PageSection size="small">
          <TeamInfo
            form={form}
            canManageAccess={teamDetailsState.canManageAccess}
          />
        </PageSection>
        <PageSection size="small">
          <TeamUsers
            teamUsersFromCurrentOrg={teamDetailsState.usersFromCurrentOrg}
            teamUsersFromOtherOrgs={teamDetailsState.usersFromOtherOrgs}
            availableUsers={availableUsers}
            teamId={teamIdQueryParam}
            canManageAccess={teamDetailsState.canManageAccess}
          />
          <Snackbar
            id="snackbar"
            open={
              updates.hasPendingUpdates ||
              // consider dirty form state only for user invite flow
              form.formState.isDirty
            }
          >
            {isNewTeam ? (
              <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.createTeamText">
                When you are ready, create the team
              </TranslationComponent>
            ) : (
              <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.updateTeamText">
                There are unsaved changes
              </TranslationComponent>
            )}
            <Button
              size="medium"
              variant="secondary"
              slot="action"
              onClick={() => {
                resetInfoForm();
                sendManagerEvent({
                  type: 'manager.teamDetails.discardUpdates',
                  teamId: teamIdQueryParam,
                });
              }}
              disabled={savingUpdates}
              i18nKey="manager/teams:teamDetails.snackbar.cancel"
            >
              Cancel
            </Button>
            <Button
              size="medium"
              variant="primary"
              slot="action"
              onClick={async () => {
                const isTeamDetailsValid = await triggerTeamInfoDetailsForm();
                if (isTeamDetailsValid) {
                  if (isNewTeam) {
                    sendManagerEvent({
                      type: 'manager.teamDetails.createNewTeam',
                      orgId: orgIdQueryParam,
                      teamInfo: getTeamInfoDetailsValues(),
                    });
                  } else {
                    sendManagerEvent({
                      type: 'manager.teamDetails.saveUpdates',
                      orgId: orgIdQueryParam,
                      teamId: teamIdQueryParam,
                      teamInfo: getTeamInfoDetailsValues(),
                    });
                  }
                }
              }}
              disabled={savingUpdates}
            >
              {(isNewTeam &&
                (savingUpdates ? (
                  <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.creating">
                    Creating Team...
                  </TranslationComponent>
                ) : (
                  <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.create">
                    Create Team
                  </TranslationComponent>
                ))) ||
                (savingUpdates ? (
                  <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.updating">
                    Saving Changes...
                  </TranslationComponent>
                ) : (
                  <TranslationComponent i18nKey="manager/teams:teamDetails.snackbar.update">
                    Save Changes
                  </TranslationComponent>
                ))}
            </Button>
          </Snackbar>
        </PageSection>
        <PageSection size="small">
          <TextTitle
            test-id="team-permissions"
            size="xsmall"
            i18nKey="manager/teams:teamDetails.permissionAndAccessTitle"
          >
            Permissions & Access
          </TextTitle>
          <TextCaption i18nKey="manager/teams:teamDetails.teamInfo.contactCenterAccess">
            Giving a Team access to a Contact Center instance creates a linked
            Team in that instance. Teams can only be managed here, not in
            Contact Center.
          </TextCaption>
          <Spacer size="large" />
          <PermissionsTable
            orgs={teamDetailsState.orgs}
            canManageAccess={teamDetailsState.canManageAccess}
            isSavingDetails={savingUpdates}
            handleAllOrgsCollapse={({ collapse }) => {
              sendManagerEvent({
                type: 'manager.teamDetails.toggleAllOrgsCollapseState',
                collapse,
              });
            }}
            handleOrgCollapse={({ orgId, collapse }) => {
              sendManagerEvent({
                type: 'manager.teamDetails.toggleOrgCollapseState',
                orgId,
                collapse,
              });
            }}
            handleAddProject={({ project }) => {
              sendManagerEvent({
                type: 'manager.teamDetails.addProject',
                project,
              });
            }}
            handleUpdateManagerRole={({ orgId, role, operation }) => {
              sendManagerEvent({
                type: 'manager.teamDetails.updateManagerRole',
                role,
                orgId,
                operation,
              });
            }}
            handleUpdateProjectProductRole={({
              orgId,
              projectId,
              role,
              operation,
            }) => {
              sendManagerEvent({
                type: 'manager.teamDetails.updateProjectProductRole',
                orgId,
                projectId,
                role,
                operation,
              });
            }}
          />
        </PageSection>
        {teamDetailsState && !isNewTeam && teamDetailsState.canManageAccess && (
          <Button
            size="large"
            variant="danger"
            disabled={loadingTeamDetails}
            onClick={() => setShowDeleteTeamConfirmationModal(true)}
            i18nKey="manager/teams:teamDetails.deleteTeam"
          >
            Delete Team
          </Button>
        )}
      </>
      <DeleteTeamConfirmationModal
        open={showDeleteTeamConfirmationModal || isDeletingTeam}
        onClose={() => setShowDeleteTeamConfirmationModal(false)}
        onDelete={() => {
          sendManagerEvent({
            type: 'manager.teamDetails.deleteTeam',
            teamId: teamIdQueryParam,
          });
          setShowDeleteTeamConfirmationModal(false);
        }}
        isDeletingTeam={isDeletingTeam}
        teamName={teamDetailsState.name}
      />
    </PageBody>
  );
};
